import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response.dto';
import { STORAGE_KEYS } from './storage-keys';
import { StorageService } from './storage.service';

/** Who is logged in right now. Memory only — never persisted (ADR-0004). */
export interface SessionProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar: string | null;
}

export interface SessionFeatureFlag {
  name: string;
  enabled: boolean;
}

export interface SessionOrganization {
  id: string;
  name: string;
  description: string;
  invitation_code: string;
  logo: string | null;
  org_type_name?: 'agency' | 'collaborative_client' | 'standard';
  feature_flags: SessionFeatureFlag[];
}

/** `null` means *not yet known*, which is the normal state for the whole boot window. */
export type OrgRole = 'owner' | 'admin' | 'member' | 'viewer' | null;

/**
 * `idle` — nothing loaded and nothing in flight (also where a cleared session lands).
 * `loading` — the two requests are out.
 * `ready` — the server answered; the session is what it says.
 * `error` — the server could not be reached. The session is intact and a retry is offered.
 */
export type SessionStatus = 'idle' | 'loading' | 'ready' | 'error';

/** The narrow slice of `GET /dashboard/` the session cares about. */
interface DashboardOrganizationPayload {
  id?: string;
  name?: string;
  description?: string;
  invitation_code?: string;
  logo?: string;
  org_type_name?: string;
  user_role?: string;
  feature_flags?: SessionFeatureFlag[];
}

interface DashboardBody {
  organization?: DashboardOrganizationPayload | null;
}

interface ProfileBody {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar?: string | null;
}

/** Every value the server may send for a role. Anything else reads as *not yet known*. */
const ORG_ROLES = ['owner', 'admin', 'member', 'viewer'] as const;

const ORG_TYPES = ['agency', 'collaborative_client', 'standard'] as const;

type OrgType = (typeof ORG_TYPES)[number];

/**
 * The base a patch merges onto when there is no organization yet. Every field is empty rather
 * than plausible: the old service defaulted the name to 'My Organization', which `getOrgInitials()`
 * happily rendered as "MY" — fabricated data indistinguishable downstream from the real thing.
 */
const EMPTY_ORGANIZATION: SessionOrganization = {
  id: '',
  name: '',
  description: '',
  invitation_code: '',
  logo: null,
  feature_flags: [],
};

/**
 * The authenticated user's identity, held in memory for the life of the tab and rebuilt from the
 * server on every app start.
 *
 * It used to be rehydrated out of `localStorage` in two service constructors, which is what made
 * stale roles possible: an admin demotes someone, they reload, and the cached `admin` paints
 * admin-only UI until the server disagrees. Identity now has exactly one source, and the cost —
 * a boot window where nothing is known yet — is paid honestly, with `null`.
 *
 * Two requests, in parallel, because neither alone is enough: `/users/me/` is the only source of
 * `avatar`, and `/dashboard/` is the only source of the organization, the role in it and the
 * feature flags. See `.scratch/session-state/backend-requirements.md` for the `/users/me/`
 * extension that would collapse this to one lightweight call.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);
  private readonly router = inject(Router);
  private readonly apiUrl = `${environment.apiHost}/api`;

  private readonly _profile = signal<SessionProfile | null>(null);
  private readonly _organization = signal<SessionOrganization | null>(null);
  private readonly _orgRole = signal<OrgRole>(null);
  private readonly _status = signal<SessionStatus>('idle');
  private readonly _error = signal<HttpErrorResponse | null>(null);

  readonly profile = this._profile.asReadonly();
  readonly organization = this._organization.asReadonly();
  readonly orgRole = this._orgRole.asReadonly();
  readonly status = this._status.asReadonly();
  /** The transport failure that stopped the last load, for the shell's retry state. */
  readonly error = this._error.asReadonly();

  readonly userName = computed(() => {
    const profile = this._profile();
    return profile ? `${profile.first_name} ${profile.last_name}`.trim() : '';
  });

  /** The account every user-scoped preference key is built from; `undefined` for the boot window. */
  readonly userId = computed(() => this._profile()?.id);

  readonly userEmail = computed(() => this._profile()?.email ?? '');

  readonly userAvatar = computed(() => this._profile()?.avatar ?? null);

  readonly userInitials = computed(() => {
    const profile = this._profile();
    return profile ? initials(profile.first_name, profile.last_name) : '';
  });

  readonly orgInitials = computed(() => initials(this._organization()?.name));

  readonly organizationId = computed(() => this._organization()?.id ?? '');
  readonly organizationName = computed(() => this._organization()?.name ?? '');
  readonly organizationLogo = computed(() => this._organization()?.logo ?? null);

  readonly hasOrganization = computed(() => !!this._organization()?.id.trim());

  /**
   * The derivation forty call sites gate on, declared once here rather than re-derived per
   * component. It fails closed: a role that has not arrived is not an admin. It used to answer
   * `true` for an unknown role, which was survivable only while the storage cache made unknown
   * rare — with the cache gone that is the whole boot window. The cost is a control appearing a
   * beat late; the alternative is one appearing wrongly.
   */
  readonly isOrganizationAdmin = computed(() => {
    const role = this._orgRole();
    return role === 'owner' || role === 'admin';
  });

  /**
   * "The server has not answered yet" — the only state in which an absent name means *not known*
   * rather than *not set*. Every skeleton in the shell hangs off this, so a failed load stops
   * shimmering and an organization the user genuinely does not have is never waited on forever.
   */
  readonly isResolving = computed(() => this._status() === 'loading');

  readonly isClientPortalAccessEnabled = computed(() => this.hasFlag('client_portal_access'));
  readonly isPreciseHoursTrackingEnabled = computed(() => this.hasFlag('precise_hours_tracking'));

  /** Shared so concurrent callers — the initializer and any guard — await one request pair. */
  private inFlight: Promise<void> | null = null;

  /**
   * Rebuilds the session from the server. Safe to call from anywhere: it no-ops without a token,
   * returns the in-flight promise when a load is already running, and does nothing once loaded.
   */
  load(): Promise<void> {
    if (this._status() === 'ready') return Promise.resolve();
    return this.reload();
  }

  /**
   * Retry after a transport failure, and the way to pick up an identity that changed
   * server-side. Same as `load()` except that it refetches even when the session is already
   * loaded — the one question that separates the two.
   */
  reload(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    if (!this.storage.getString(STORAGE_KEYS.auth.access)) return Promise.resolve();
    return this.start();
  }

  /*
   * Local edits. Settings changes a name or an avatar, onboarding creates an organization, and
   * the shell has to reflect that immediately. They write to the signals only — the server stays
   * the source of truth, and the next `reload()` overwrites whatever these set.
   */

  setProfile(profile: SessionProfile | null): void {
    this._profile.set(profile);
  }

  /** No-op when there is no profile: a patch cannot invent an identity the server has not sent. */
  patchProfile(changes: Partial<SessionProfile>): void {
    this._profile.update((current) => (current ? { ...current, ...changes } : null));
  }

  /** Merges onto a blank organization when there is none, so onboarding can create one. */
  patchOrganization(changes: Partial<SessionOrganization>): void {
    this._organization.update((current) => ({ ...(current ?? EMPTY_ORGANIZATION), ...changes }));
  }

  setOrgRole(role: OrgRole): void {
    this._orgRole.set(role);
  }

  clearOrganization(): void {
    this._organization.set(null);
    this._orgRole.set(null);
  }

  clear(): void {
    this._profile.set(null);
    this._organization.set(null);
    this._orgRole.set(null);
    this._error.set(null);
    this._status.set('idle');
  }

  /**
   * The returned promise never rejects. Guards await it, and a `CanActivateFn` that rejects
   * fails the navigation outright — a malformed payload would strand the user on a blank route
   * rather than showing them the error state.
   */
  private start(): Promise<void> {
    this._status.set('loading');
    this._error.set(null);
    this.inFlight = this.run()
      .catch((reason: unknown) => {
        this._error.set(toFailure(reason));
        this._status.set('error');
      })
      .finally(() => {
        this.inFlight = null;
      });
    return this.inFlight;
  }

  /**
   * Both requests go out together and each lands on its own signal the moment it arrives, so a
   * slow `/dashboard/` does not hold back the name in the header. Nothing here rejects: a failed
   * load is a state the shell renders, not an exception for callers to handle.
   */
  private async run(): Promise<void> {
    const profile = firstValueFrom(this.http.get<{ data?: ProfileBody }>(`${this.apiUrl}/users/me/`)).then(
      (response) => this.applyProfile(response?.data),
      toFailure,
    );
    const dashboard = firstValueFrom(
      this.http.get<ApiResponse<DashboardBody> | DashboardBody>(`${this.apiUrl}/dashboard/`),
    ).then((response) => this.applyOrganization(unwrapDashboard(response)), toFailure);

    const failures = (await Promise.all([profile, dashboard])).filter(
      (failure): failure is HttpErrorResponse => failure != null,
    );

    if (failures.some(isUnauthorized)) {
      this.rejectSession();
      return;
    }
    if (failures.length > 0) {
      // Unreachable, not unauthorized. The session stays exactly as it is and the shell offers
      // a retry — the old behaviour told a user with a perfectly good organization to create one.
      this._error.set(failures[0]);
      this._status.set('error');
      return;
    }
    this._status.set('ready');
  }

  private applyProfile(body: ProfileBody | undefined): void {
    if (!body) return;
    this._profile.set({
      id: body.id,
      first_name: body.first_name,
      last_name: body.last_name,
      email: body.email,
      avatar: body.avatar ?? null,
    });
  }

  private applyOrganization(body: DashboardBody | undefined): void {
    const organization = body?.organization;
    if (!organization?.id) {
      this._organization.set(null);
      this._orgRole.set(null);
      return;
    }
    this._organization.set({
      id: organization.id,
      name: organization.name ?? '',
      description: organization.description ?? '',
      invitation_code: organization.invitation_code ?? '',
      logo: organization.logo || null,
      org_type_name: toOrgType(organization.org_type_name),
      feature_flags: organization.feature_flags ?? [],
    });
    this._orgRole.set(toOrgRole(organization.user_role));
  }

  /** The token is dead or the account lost access: end the session rather than retry into it. */
  private rejectSession(): void {
    this.clear();
    this.storage.remove(STORAGE_KEYS.auth.access);
    this.storage.remove(STORAGE_KEYS.auth.refresh);
    this.router.navigate(['/auth/login']);
  }

  private hasFlag(name: string): boolean {
    return (this._organization()?.feature_flags ?? []).some((flag) => flag.name === name && flag.enabled);
  }
}

/**
 * Two letters from a name: one from each of the first two words, or the first two letters of a
 * single word. Used for both people and organizations, which want the same answer.
 */
function initials(...words: (string | undefined)[]): string {
  const [first, second] = words.flatMap((word) => word?.trim().split(/\s+/) ?? []).filter((word) => word.length > 0);
  if (!first) return '';
  return (second ? first[0] + second[0] : first.substring(0, 2)).toUpperCase();
}

/** Exported for the callers that receive a role as a bare string from the server. */
export function toOrgRole(role: string | null | undefined): OrgRole {
  return ORG_ROLES.find((known) => known === role) ?? null;
}

/**
 * Validated rather than cast: an unrecognised type reads as absent, so a backend typo shows up
 * as "no special org type" instead of as a literal the union says cannot exist.
 */
function toOrgType(type: string | undefined): OrgType | undefined {
  return ORG_TYPES.find((known) => known === type);
}

/** `GET /dashboard/` answers with or without the `ApiResponse` envelope depending on the path. */
function unwrapDashboard(body: { data?: DashboardBody } | DashboardBody | undefined): DashboardBody | undefined {
  if (body && 'data' in body && body.data != null && typeof body.data === 'object') {
    return body.data;
  }
  return body as DashboardBody | undefined;
}

/** Every rejection becomes an `HttpErrorResponse`, so callers have one shape to read a status off. */
function toFailure(reason: unknown): HttpErrorResponse {
  return reason instanceof HttpErrorResponse
    ? reason
    : new HttpErrorResponse({ status: 0, statusText: 'Unknown error' });
}

/** The token is dead, or the account lost access. Either way, retrying into it is pointless. */
function isUnauthorized(failure: HttpErrorResponse): boolean {
  return failure.status === 401 || failure.status === 403;
}
